/**
 * hero.ts
 * Hero texts (CMS) and Hero media cache management.
 */

import { cache, heroInFlight, isStale, notify, ALL_PAGE_KEYS, ALL_LANGS } from './base';

const LS_HERO_PREFIX = 'sargo_hero_';
const LS_HERO_MEDIA_KEY = 'sargo_hero_media';

interface StoredHeroEntry {
  data: { eyebrow: string; title: string; subtitle: string };
  fetchedAt: number;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function loadHeroFromStorage(pageKey: string, lang: string): StoredHeroEntry | null {
  try {
    const raw = localStorage.getItem(`${LS_HERO_PREFIX}${pageKey}:${lang}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object' && 'fetchedAt' in parsed) {
      return parsed as StoredHeroEntry;
    }
    if (parsed && typeof parsed.eyebrow !== 'undefined') {
      return { data: parsed, fetchedAt: 0 };
    }
    return null;
  } catch {
    return null;
  }
}

function saveHeroToStorage(pageKey: string, lang: string, data: { eyebrow: string; title: string; subtitle: string }, fetchedAt: number): void {
  try {
    const entry: StoredHeroEntry = { data, fetchedAt };
    localStorage.setItem(`${LS_HERO_PREFIX}${pageKey}:${lang}`, JSON.stringify(entry));
  } catch { /**/ }
}

// ─── Hero Texts ──────────────────────────────────────────────────────────────

export function getCachedHero(pageKey: string, lang: string) {
  const key = `${pageKey}:${lang}`;
  return cache.heroTexts.get(key)?.data ?? null;
}

export async function fetchHeroTexts(
  pageKey: string,
  lang: string,
  fallbacks: { eyebrow?: string; title?: string; subtitle?: string } = {}
): Promise<{ eyebrow: string; title: string; subtitle: string }> {
  const key = `${pageKey}:${lang}`;
  const entry = cache.heroTexts.get(key);

  if (entry && !isStale(entry)) return entry.data;
  if (heroInFlight.has(key)) return heroInFlight.get(key)!;

  const { getHeroTexts } = await import('../cms-hero');
  const promise = getHeroTexts(pageKey, lang, fallbacks).then((data) => {
    const fetchedAt = Date.now();
    cache.heroTexts.set(key, { data, fetchedAt });
    saveHeroToStorage(pageKey, lang, data, fetchedAt);
    heroInFlight.delete(key);
    notify();
    return data;
  }).catch(() => {
    heroInFlight.delete(key);
    const existing = cache.heroTexts.get(key)?.data;
    return existing ?? { eyebrow: fallbacks.eyebrow ?? '', title: fallbacks.title ?? '', subtitle: fallbacks.subtitle ?? '' };
  });

  heroInFlight.set(key, promise);
  return promise;
}

export function saveHeroTextsToCache(pageKey: string, lang: string, data: { eyebrow: string; title: string; subtitle: string }): void {
  const fetchedAt = Date.now();
  cache.heroTexts.set(`${pageKey}:${lang}`, { data, fetchedAt });
  saveHeroToStorage(pageKey, lang, data, fetchedAt);
}

export function invalidateHeroCache(pageKey?: string, lang?: string) {
  if (pageKey && lang) {
    cache.heroTexts.delete(`${pageKey}:${lang}`);
    try { localStorage.removeItem(`${LS_HERO_PREFIX}${pageKey}:${lang}`); } catch { /**/ }
  } else if (pageKey) {
    for (const key of [...cache.heroTexts.keys()]) {
      if (key.startsWith(`${pageKey}:`)) {
        cache.heroTexts.delete(key);
        try { localStorage.removeItem(`${LS_HERO_PREFIX}${key}`); } catch { /**/ }
      }
    }
    if (pageKey === 'home') {
      cache.heroMedia = null;
      try { localStorage.removeItem(LS_HERO_MEDIA_KEY); } catch { /**/ }
    }
  } else {
    cache.heroTexts.clear();
    cache.heroMedia = null;
    try {
      localStorage.removeItem(LS_HERO_MEDIA_KEY);
      const toRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (k && k.startsWith(LS_HERO_PREFIX)) toRemove.push(k);
      }
      toRemove.forEach(k => localStorage.removeItem(k));
    } catch { /**/ }
  }
}

// ─── Hero Media ──────────────────────────────────────────────────────────────

export function getCachedHeroMedia() {
  return cache.heroMedia?.data ?? null;
}

export function saveHeroMediaToCache(media: any[]): void {
  const fetchedAt = Date.now();
  cache.heroMedia = { data: media, fetchedAt };
  try {
    localStorage.setItem(LS_HERO_MEDIA_KEY, JSON.stringify({ data: media, fetchedAt }));
  } catch { /**/ }
}

export async function fetchHeroMedia(): Promise<any[]> {
  if (cache.heroMedia && !isStale(cache.heroMedia)) {
    return cache.heroMedia.data;
  }
  const { getHeroMedia } = await import('../cms-hero');
  try {
    const data = await getHeroMedia();
    saveHeroMediaToCache(data);
    notify();
    return data;
  } catch (e) {
    console.error("Failed to fetch hero media:", e);
    return cache.heroMedia?.data ?? [];
  }
}

// ─── Initialization ──────────────────────────────────────────────────────────
// Synchronously hydrate the in-memory cache from localStorage at module load.
// This is the difference between "blank hero for 300-1500ms while DB fetch
// resolves" vs. "hero appears instantly with last-known image, then silently
// refreshes if data changed". The DB fetch still runs after mount and
// overwrites the cached value if it's different.

(function preloadHeroFromStorage() {
  if (typeof localStorage === 'undefined') return;

  for (const pageKey of ALL_PAGE_KEYS) {
    for (const lang of ALL_LANGS) {
      const entry = loadHeroFromStorage(pageKey, lang);
      if (entry) {
        cache.heroTexts.set(`${pageKey}:${lang}`, { data: entry.data, fetchedAt: entry.fetchedAt });
      }
    }
  }

  try {
    const raw = localStorage.getItem(LS_HERO_MEDIA_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && 'data' in parsed && 'fetchedAt' in parsed) {
        cache.heroMedia = { data: parsed.data, fetchedAt: parsed.fetchedAt };
      }
    }
  } catch { /**/ }
})();
